import React, { useState, useEffect } from 'react';
import { useUser } from '../userContext';
import { useClient } from '../clientContext';
import { doc, getDoc, updateDoc, writeBatch, collection, query, where, getDocs, arrayUnion, arrayRemove } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { BuildingOfficeIcon, PencilIcon, TrashIcon, UsersIcon } from '@heroicons/react/24/outline';

const functions = getFunctions(undefined, 'europe-west1');
const createWorkspaceCallable = httpsCallable(functions, 'createWorkspace');

export default function Workspaces() {
  const navigate = useNavigate();
  const { businessProfile, switchActiveClient, userProfile } = useUser();
  // Subscriptions retired: multi-workspace + upgrade modal both no-ops.
  const canUseMultipleWorkspaces = false;
  const openUpgradeModal = () => {};
  const { clients, refreshClients } = useClient();

  const isImpersonating = businessProfile?.id && userProfile?.companyId && businessProfile.id !== userProfile.companyId;

  // Create state
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Inline edit state
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Delete state
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const [error, setError] = useState('');

  // Member management state
  const [expandedId, setExpandedId] = useState(null);
  const [teamUsers, setTeamUsers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [togglingMember, setTogglingMember] = useState(null);
  const [agencyOwnerId, setAgencyOwnerId] = useState(null);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newWorkspaceName.trim()) return;
    setIsCreating(true);
    setCreateError('');
    try {
      const { data } = await createWorkspaceCallable({ name: newWorkspaceName.trim() });
      if (!data?.workspaceId) throw new Error('No workspace ID returned');
      await refreshClients();
      setNewWorkspaceName('');
    } catch (err) {
      const message = err?.message || err?.code;
      const isEntitlement = message && (message.includes('Operations') || message.includes('agency') || message.includes('resource-exhausted') || err?.code === 'functions/resource-exhausted');
      setCreateError(isEntitlement ? 'Unlimited workspaces require the Team plan. Upgrade from Plans.' : 'Could not create workspace. Try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const startEditing = (client) => {
    setEditingId(client.id);
    setEditName(client.name);
    setError('');
    setDeletingId(null);
  };

  const handleRename = async (clientId) => {
    const nextName = editName.trim();
    if (!nextName) return;
    setIsSaving(true);
    setError('');
    try {
      await updateDoc(doc(db, 'workspace', clientId), { name: nextName });
      await refreshClients();
      setEditingId(null);
    } catch (err) {
      setError('Could not rename workspace. Try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const startDeleting = (client) => {
    setDeletingId(client.id);
    setDeleteConfirm('');
    setError('');
    setEditingId(null);
  };

  const handleDelete = async (client) => {
    if (deleteConfirm.trim() !== client.name) {
      setError('Type the workspace name to confirm.');
      return;
    }
    setIsDeleting(true);
    setError('');
    try {
      const batch = writeBatch(db);
      const campaignsQuery = query(
        collection(db, 'campaign'),
        where('companyId', '==', client.id)
      );
      const snap = await getDocs(campaignsQuery);
      snap.forEach((d) => batch.update(d.ref, { status: 'paused', pausedAt: new Date() }));
      batch.delete(doc(db, 'workspace', client.id));
      await batch.commit();
      await refreshClients();
      if (businessProfile?.id === client.id) {
        await switchActiveClient(null);
        navigate('/studio/dashboard');
      }
      setDeletingId(null);
      setDeleteConfirm('');
    } catch (err) {
      setError('Could not delete workspace. Try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Fetch team users and agency owner for member management
  useEffect(() => {
    const load = async () => {
      if (!userProfile?.companyId) return;
      setLoadingMembers(true);
      try {
        const [usersSnap, agencyDoc] = await Promise.all([
          getDocs(query(collection(db, 'users'), where('companyId', '==', userProfile.companyId))),
          getDoc(doc(db, 'workspace', userProfile.companyId)),
        ]);
        setTeamUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        if (agencyDoc.exists()) setAgencyOwnerId(agencyDoc.data().ownerId);
      } catch (err) {
        console.error('Error loading team members:', err);
      } finally {
        setLoadingMembers(false);
      }
    };
    load();
  }, [userProfile?.companyId]);

  const toggleMember = async (workspace, userId) => {
    const workspaceId = workspace.id;
    const currentMembers = workspace.members || [];
    const hasMembersRestriction = currentMembers.length > 0;
    const isMember = !hasMembersRestriction || currentMembers.includes(userId);

    setTogglingMember(`${workspaceId}_${userId}`);
    try {
      if (!hasMembersRestriction && isMember) {
        // Transitioning from unrestricted → restricted: add all users except this one
        const allUids = teamUsers.map(u => u.id).filter(id => id !== userId);
        await updateDoc(doc(db, 'workspace', workspaceId), { members: allUids });
      } else if (isMember) {
        await updateDoc(doc(db, 'workspace', workspaceId), { members: arrayRemove(userId) });
      } else {
        await updateDoc(doc(db, 'workspace', workspaceId), { members: arrayUnion(userId) });
      }
      await refreshClients();
    } catch (err) {
      console.error('Error toggling member:', err);
      setError('Could not update member access. Try again.');
    } finally {
      setTogglingMember(null);
    }
  };

  if (!canUseMultipleWorkspaces) {
    return (
      <div className="w-full px-8 py-6">
        <div className="rounded-xl p-6 bg-white/5 border border-[var(--admin-border)]">
          <h3 className="text-base font-semibold gch-text-primary">
            Workspaces require the Team plan
          </h3>
          <p className="text-sm gch-text-secondary font-medium mt-1 mb-4">
            Organise campaigns by brand or client with separate workspaces, each with its own settings and analytics. Available on the Team plan.
          </p>
          <ul className="space-y-2 mb-5">
            <li className="flex items-start gap-2 text-sm gch-text-secondary">
              <BuildingOfficeIcon className="w-4 h-4 flex-shrink-0 mt-0.5 text-[var(--admin-accent)]" />
              <span>Unlimited workspaces for brands or clients</span>
            </li>
            <li className="flex items-start gap-2 text-sm gch-text-secondary">
              <BuildingOfficeIcon className="w-4 h-4 flex-shrink-0 mt-0.5 text-[var(--admin-accent)]" />
              <span>Per-workspace campaign separation and analytics</span>
            </li>
            <li className="flex items-start gap-2 text-sm gch-text-secondary">
              <BuildingOfficeIcon className="w-4 h-4 flex-shrink-0 mt-0.5 text-[var(--admin-accent)]" />
              <span>Control team member access per workspace</span>
            </li>
          </ul>
          <button
            type="button"
            onClick={openUpgradeModal}
            className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-lg btn-admin-primary"
          >
            Upgrade to Team
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-8 py-6">
      <h2 className="text-lg font-semibold tracking-tight gch-text-primary">Workspaces</h2>
      <p className="mt-1 max-w-2xl text-sm leading-6 font-medium gch-text-secondary">
        Manage your workspaces. Each workspace has its own campaigns, analytics, and settings.
      </p>

      {/* Workspace list */}
      <div className="mt-6 space-y-2">
        {clients.map((client) => {
          const isExpanded = expandedId === client.id;
          const hasMembersRestriction = client.members && client.members.length > 0;
          const hasTeam = teamUsers.length > 0;

          return (
            <div key={client.id} className="rounded-xl border border-[var(--admin-border)] bg-white/[0.02] overflow-hidden">
              {/* Workspace row */}
              <div className="flex items-center gap-3 px-4 py-3">
                {editingId === client.id ? (
                  <form onSubmit={(e) => { e.preventDefault(); handleRename(client.id); }} className="flex items-center gap-2 flex-1 min-w-0">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="flex-1 rounded-lg gch-input-ghost border border-[var(--admin-border)] px-3 py-1.5 text-sm gch-text-primary focus:outline-none focus:ring-2 focus:ring-[var(--admin-accent)]"
                      autoFocus
                      maxLength={50}
                    />
                    <button type="submit" disabled={isSaving} className="btn-admin-primary btn-admin-sm text-xs disabled:opacity-50">
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button type="button" onClick={() => setEditingId(null)} className="btn-admin-outline btn-admin-sm text-xs">
                      Cancel
                    </button>
                  </form>
                ) : deletingId === client.id ? (
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-red-400 mb-2">
                      Type <strong className="text-red-300">{client.name}</strong> to confirm deletion. This will pause all campaigns and remove the workspace.
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={deleteConfirm}
                        onChange={(e) => setDeleteConfirm(e.target.value)}
                        placeholder={client.name}
                        className="flex-1 rounded-lg gch-input-ghost border border-red-400/30 px-3 py-1.5 text-sm gch-text-primary focus:outline-none focus:ring-2 focus:ring-red-400/50"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => handleDelete(client)}
                        disabled={isDeleting || deleteConfirm.trim() !== client.name}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-500 disabled:opacity-50 transition-colors"
                      >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                      </button>
                      <button type="button" onClick={() => { setDeletingId(null); setDeleteConfirm(''); }} className="btn-admin-outline btn-admin-sm text-xs">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <BuildingOfficeIcon className="h-5 w-5 flex-shrink-0 gch-text-muted" />
                    <div className="flex-1 min-w-0">
                      <span className="block truncate text-sm font-medium gch-text-primary">{client.name}</span>
                      {hasTeam && (
                        <span className="text-[10px] gch-text-muted">
                          {hasMembersRestriction ? `${client.members.length} of ${teamUsers.length} members` : 'All members'}
                        </span>
                      )}
                    </div>
                    {businessProfile?.id === client.id && (
                      <span className="text-[10px] font-medium gch-text-muted">current</span>
                    )}
                    {hasTeam && (
                      <button
                        type="button"
                        onClick={() => { setExpandedId(isExpanded ? null : client.id); setEditingId(null); setDeletingId(null); }}
                        className={`p-1.5 rounded-lg transition-colors ${isExpanded ? 'text-[var(--admin-accent)] bg-[var(--admin-accent-light)]' : 'gch-text-muted hover:gch-text-primary hover:bg-white/5'}`}
                        title="Manage members"
                      >
                        <UsersIcon className="h-4 w-4" />
                      </button>
                    )}
                    <button type="button" onClick={() => { startEditing(client); setExpandedId(null); }} className="p-1.5 rounded-lg gch-text-muted hover:gch-text-primary hover:bg-white/5 transition-colors" title="Rename">
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => { startDeleting(client); setExpandedId(null); }} className="p-1.5 rounded-lg gch-text-muted hover:text-red-400 hover:bg-white/5 transition-colors" title="Delete">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>

              {/* Member access — inside the same card, separated by border */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-3 border-t border-[var(--admin-border)]">
                  {loadingMembers ? (
                    <p className="text-xs gch-text-muted py-1">Loading team...</p>
                  ) : (
                    <div className="space-y-1.5">
                      {teamUsers.map(u => {
                        const isOwnerUser = u.id === agencyOwnerId;
                        const isMember = !hasMembersRestriction || client.members.includes(u.id);
                        const isToggling = togglingMember === `${client.id}_${u.id}`;

                        return (
                          <div key={u.id} className="flex items-center gap-3 py-1">
                            <button
                              type="button"
                              onClick={() => !isOwnerUser && toggleMember(client, u.id)}
                              disabled={isOwnerUser || isToggling}
                              className={`relative flex-shrink-0 w-8 h-[18px] rounded-full transition-colors duration-200 ${
                                isMember ? 'bg-[var(--admin-accent)]' : 'bg-white/10'
                              } ${isOwnerUser ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                              aria-label={`${isMember ? 'Remove' : 'Add'} ${u.firstName || u.email} access`}
                            >
                              <span className={`absolute top-0.5 left-0.5 w-3.5 h-3.5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                                isMember ? 'translate-x-[14px]' : 'translate-x-0'
                              }`} />
                            </button>
                            <div className="flex-1 min-w-0">
                              <span className="text-xs font-medium gch-text-primary truncate block">
                                {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email}
                              </span>
                              {u.email && u.firstName && (
                                <span className="text-[10px] gch-text-muted truncate block">{u.email}</span>
                              )}
                            </div>
                            {isOwnerUser && (
                              <span className="text-[10px] gch-text-muted flex-shrink-0">Owner</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {clients.length === 0 && (
          <p className="text-sm gch-text-muted py-3">No workspaces yet. Create one below.</p>
        )}
      </div>

      {error && (
        <div className="mt-3 p-3 rounded-lg bg-red-500/15 border border-red-400/30 text-sm text-red-300">{error}</div>
      )}

      {/* Create workspace */}
      <div className="mt-6 pt-6 border-t border-[var(--admin-border)]">
        <h3 className="text-sm font-semibold gch-text-primary mb-3">Create workspace</h3>
        <form onSubmit={handleCreate} className="flex items-end gap-3">
          <div className="flex-1 max-w-sm">
            <label htmlFor="new-workspace-name" className="block text-xs font-medium gch-text-secondary mb-1.5">
              Workspace name
            </label>
            <input
              id="new-workspace-name"
              type="text"
              value={newWorkspaceName}
              onChange={(e) => setNewWorkspaceName(e.target.value)}
              maxLength={50}
              className="w-full rounded-lg gch-input-ghost border border-[var(--admin-border)] px-3 py-2 text-sm gch-text-primary placeholder:gch-text-muted focus:outline-none focus:ring-2 focus:ring-[var(--admin-accent)]"
              placeholder="e.g. Acme Events"
              required
            />
          </div>
          <button type="submit" disabled={isCreating} className="btn-admin-primary btn-admin-md disabled:opacity-50">
            {isCreating ? 'Creating...' : 'Create'}
          </button>
        </form>
        {createError && (
          <div className="mt-3 p-3 rounded-lg bg-red-500/15 border border-red-400/30 text-sm text-red-300">{createError}</div>
        )}
      </div>
    </div>
  );
}
